import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/i18n/LanguageContext";
import { Activity, ExternalLink, Monitor, RefreshCw } from "lucide-react";
import { StatusBadge } from "./StatusBadge";

export function StatusCard({
  connectionStatus,
  isRecording,
  openTranslationWindow,
  openTranslationOnSecondScreen,
  hasSecondScreen,
  secondScreenInfo,
  requestScreenPermission
}) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 md:col-span-5 bento-card" data-testid="status-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Activity className="w-5 h-5" />
          {t("status")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-center">
          <StatusBadge connectionStatus={connectionStatus} />
        </div>
        
        <div className="text-center text-zinc-400 text-sm">
          {isRecording ? (
            <span>{t("processingAudio")}</span>
          ) : (
            <span>{t("clickToStart")}</span>
          )}
        </div>

        <Button
          onClick={openTranslationWindow}
          variant="outline"
          className="w-full border-zinc-700 hover:bg-zinc-800 text-white"
          data-testid="open-translation-window"
          aria-label={t("openTranslationWindow")}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          {t("openTranslationWindow")}
        </Button>

        {/* Second Screen Button */}
        {hasSecondScreen && (
          <Button
            onClick={openTranslationOnSecondScreen}
            variant="outline"
            className="w-full border-blue-600 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 mt-2 text-sm sm:text-base"
            data-testid="open-second-screen"
            aria-label={t("openSecondScreen")}
          >
            <Monitor className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{t("openSecondScreen")}</span>
            {secondScreenInfo?.label && (
              <span className="ml-1 text-xs opacity-70 hidden sm:inline">({secondScreenInfo.label})</span>
            )}
          </Button>
        )}

        {/* Screen Detection Status */}
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Monitor className={`w-4 h-4 flex-shrink-0 ${hasSecondScreen ? 'text-green-400' : 'text-zinc-500'}`} />
              <span className={`text-xs ${hasSecondScreen ? 'text-green-400' : 'text-zinc-500'}`}>
                {hasSecondScreen 
                  ? `${t("secondScreenDetected")}${secondScreenInfo?.label ? `: ${secondScreenInfo.label}` : ''}`
                  : t("noSecondScreen")
                }
              </span>
            </div>
            <Button
              onClick={requestScreenPermission}
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-zinc-400 hover:text-white"
              data-testid="refresh-screens"
              aria-label={t("detect")}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              {t("detect")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
